/**
 * Agent permissions. A person's rights on an agent are the grants of every role they hold there,
 * added together. Safe for client and server; the client use only shapes the UI.
 * See docs/auth-permissions.md, "RBAC".
 */
import { canEditPath, canWritePath, isKnowledgePattern, KNOWLEDGE_GLOBS, validateEditPaths, type WriteScope } from '../versions/editScope';
import { validateViewPatterns, viewPatternsMatch, type ViewTarget } from './viewRules';

export type { ViewTarget } from './viewRules';

export const PERMISSIONS = [
	'versions:read',
	'knowledge:write',
	'code:write',
	'drafts:delete',
	'versions:publish',
	'test_users:create',
	'evals:run',
	'conversations:rate',
	'users:create',
	'views:create',
	'view:update',
	'view:delete',
	'users:read',
	'users:update',
	'users:delete',
	'users:export',
	'data:analyze',
	'actions:run',
	'logs:read',
	'actions:automate',
	'agent:settings',
	'integrations:manage',
	'api_keys:manage',
	'webhooks:manage',
	'fields:manage',
	'metrics:manage',
	'members:invite',
	'members:manage',
	'workspace:settings',
	'workspace:billing',
	'agents:create',
	'agents:manage',
	'agents:delete',
	'workspaces:manage',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

/**
 * Workspace permissions count only from assignments for all agents (`agent_id` null), so a role held on
 * one agent never reaches workspace settings or billing. `members:invite` and `members:manage` work on both
 * levels: on one agent for its role assignments, workspace-wide for invites, removals and custom roles.
 */
export const WORKSPACE_PERMISSIONS = [
	'workspace:settings',
	'workspace:billing',
	'agents:create',
	'agents:manage',
	'agents:delete',
	'workspaces:manage',
] as const satisfies readonly Permission[];
export type WorkspacePermission = (typeof WORKSPACE_PERMISSIONS)[number];

export function isWorkspacePermission(permission: string): permission is WorkspacePermission {
	return (WORKSPACE_PERMISSIONS as readonly string[]).includes(permission);
}
export type RbacRole = 'agent_member' | 'agent_admin' | 'agent_superadmin';

/** Permissions a grant can tie to views with `views`. Without `views` a grant covers every view. */
export const VIEW_PERMISSIONS = ['users:read', 'users:update', 'users:delete', 'users:export', 'view:update', 'view:delete', 'views:create'] as const satisfies readonly Permission[];
export type ViewPermission = (typeof VIEW_PERMISSIONS)[number];

/**
 * Old names, still accepted on save and in stored roles; each stands for these grants (on every view and
 * every action). `versions:write` is split apart by `splitLegacyWrite`. See docs/auth-permissions.md, "Old names".
 */
export const LEGACY_PERMISSIONS: Record<string, readonly Permission[]> = {
	'views:manage': ['views:create', 'view:update', 'view:delete'],
	'data:edit': ['users:create', 'users:update', 'users:export'],
	'conversations:delete': ['users:delete'],
	'preview:run': ['versions:read'],
	'tools:run': ['versions:read'],
	'subagents:manage': ['code:write'],
	'versions:promote': ['versions:publish', 'drafts:delete'],
	'training:manage': ['test_users:create'],
	'actions:read': [],
	'actions:write': ['actions:run', 'actions:automate'],
	'roles:manage': ['members:manage'],
	'knowledge:manage': ['workspace:settings'],
	'billing:manage': ['workspace:billing'],
	'connections:manage': ['integrations:manage'],
	'channels:manage': ['integrations:manage'],
	'secrets:write': ['integrations:manage'],
	'integrations:read': ['integrations:manage'],
	'integrations:write': ['integrations:manage'],
};

/**
 * One permission, optionally tied to tree paths (`knowledge:write` only, inside `KNOWLEDGE_GLOBS`), to views
 * (`VIEW_PERMISSIONS` only) or to actions (`actions:run` only).
 */
export interface Grant {
	permission: Permission;
	paths?: string[];
	/** View slugs or patterns (`*`, `sales-*`, `sales/**`). See `viewPatternMatches`. */
	views?: string[];
	/** Action ids. Absent means every action. */
	actions?: string[];
}

export interface Access {
	grants: Grant[];
	/** The highest of the three legacy roles held on this agent; feeds `agentRole` for the `canX(role)` helpers. */
	builtinRole: RbacRole | null;
}

/** Actions an Agent member runs: the ones whose manifests listed agent_member before roles owned actions. */
export const MEMBER_ACTIONS = ['bulk-update-field', 'schedule-appointment', 'set-office-location', 'opt-out-user', 'log-event'];

export const ROLE_PERMISSIONS: Record<RbacRole, readonly Permission[]> = {
	agent_member: ['versions:read', 'test_users:create', 'conversations:rate', 'users:read', 'actions:run'],
	agent_admin: [
		'versions:read',
		'knowledge:write',
		'code:write',
		'versions:publish',
		'test_users:create',
		'evals:run',
		'conversations:rate',
		'users:create',
		'views:create',
		'view:update',
		'view:delete',
		'users:read',
		'users:update',
		'users:delete',
		'users:export',
		'data:analyze',
		'actions:run',
		'logs:read',
		'actions:automate',
		'integrations:manage',
		'fields:manage',
		'metrics:manage',
	],
	agent_superadmin: PERMISSIONS.filter((p) => !isWorkspacePermission(p)),
};

export type BuiltinRoleKey = RbacRole | 'content_editor' | 'code_builder' | 'workspace_superadmin';

export interface BuiltinRole {
	key: BuiltinRoleKey;
	name: string;
	description: string;
	grants: Grant[];
}

const grantsOf = (permissions: readonly Permission[]): Grant[] => permissions.map((permission) => ({ permission }));
const memberGrants = (): Grant[] => ROLE_PERMISSIONS.agent_member.map((permission) => (permission === 'actions:run' ? { permission, actions: [...MEMBER_ACTIONS] } : { permission }));

/** Upserted into `roles` by `RoleRepo.upsertBuiltinRoles`; these lists, not the rows, are what `can` uses. */
export const BUILTIN_ROLES: Record<BuiltinRoleKey, BuiltinRole> = {
	agent_superadmin: {
		key: 'agent_superadmin',
		name: 'Agent superadmin',
		description: 'Everything on the agent, including deleting drafts, settings, channels, API keys, webhooks and members.',
		grants: grantsOf(ROLE_PERMISSIONS.agent_superadmin),
	},
	agent_admin: {
		key: 'agent_admin',
		name: 'Agent admin',
		description: 'Edits and publishes versions, runs evals and actions, reads logs, sets up view actions, and manages channels, integrations, views, fields and Users.',
		grants: grantsOf(ROLE_PERMISSIONS.agent_admin),
	},
	agent_member: {
		key: 'agent_member',
		name: 'Agent member',
		description: 'Previews the agent, makes sandbox users, rates and discusses AI replies, and runs a few everyday actions.',
		grants: memberGrants(),
	},
	workspace_superadmin: {
		key: 'workspace_superadmin',
		name: 'Workspace superadmin',
		description: 'Everything in the workspace: every agent, members, roles, billing, knowledge bases and workspace settings. Give it for all agents.',
		grants: grantsOf(PERMISSIONS),
	},
	content_editor: {
		key: 'content_editor',
		name: 'Content editor',
		description: 'Edits the text of prompts and knowledge files. A full admin publishes the changes.',
		grants: [
			...grantsOf(['versions:read', 'test_users:create', 'users:read', 'conversations:rate']),
			{ permission: 'knowledge:write', paths: [...KNOWLEDGE_GLOBS] },
		],
	},
	code_builder: {
		key: 'code_builder',
		name: 'Code builder',
		description: 'Edits agent code (tools, handlers, manifest, subagent settings) and tests it. A full admin publishes the changes.',
		grants: grantsOf(['versions:read', 'test_users:create', 'code:write']),
	},
};

const RANK: Record<RbacRole, number> = { agent_member: 1, agent_admin: 2, agent_superadmin: 3 };

export function isRbacRole(key: unknown): key is RbacRole {
	return typeof key === 'string' && Object.hasOwn(RANK, key);
}

export function higherRole(a: RbacRole | null, b: RbacRole | null): RbacRole | null {
	if (!a || !b) return a ?? b;
	return RANK[a] >= RANK[b] ? a : b;
}

/**
 * Whether a built-in role is at least `min`. Only for content that names built-in roles (view
 * `allowGroups`, field `x-visibility`, `superadminOnly`, page and action `roles`); every other
 * check uses `can`. See docs/auth-permissions.md, "Content visibility stays on built-in roles".
 */
export function roleAtLeast(role: RbacRole | null | undefined, min: RbacRole): boolean {
	return !!role && RANK[role] >= RANK[min];
}

/** Not counted when matching a tier: custom roles from before these existed lack them. */
const TIER_IGNORED: readonly Permission[] = ['conversations:rate', 'metrics:manage', 'data:analyze'];

/**
 * The highest built-in role whose permissions `access` covers, never below `builtinRole`. Picks the assistant
 * tier for custom roles. Publish, promote and code edits don't count against path-limited writers, who never
 * hold them, so a 129-style limited admin stays on the admin tier with narrowed writes.
 */
export function coveredRole(access: Access): RbacRole {
	const held = new Set<Permission>([...permissionsFor(access), ...TIER_IGNORED]);
	if (knowledgePathsOf(access)?.length) held.add('versions:publish').add('drafts:delete').add('code:write');
	const covered = (['agent_superadmin', 'agent_admin'] as const).find((r) => ROLE_PERMISSIONS[r].every((p) => held.has(p))) ?? 'agent_member';
	return access.builtinRole && RANK[access.builtinRole] > RANK[covered] ? access.builtinRole : covered;
}

/**
 * What the sidebar assistant may do for this person. The tier gates draft writes: a member-tier turn
 * (`coveredRole` below admin) loses its code and knowledge write grants; inside an admin tier, path grants
 * only narrow. Their grants still apply in the Code panel and the version routes.
 */
export function assistantAccess(access: Access): Access {
	if (roleAtLeast(coveredRole(access), 'agent_admin')) return access;
	return { ...access, grants: access.grants.filter((g) => !isWriteGrant(g.permission)) };
}

const isWriteGrant = (permission: Permission) => permission === 'code:write' || permission === 'knowledge:write';

export function accessFor(role: RbacRole): Access {
	return { grants: BUILTIN_ROLES[role].grants, builtinRole: role };
}

/** Every permission, workspace ones included: a workspace superadmin, or the workspace owner (the lockout backstop). */
export function fullAccess(): Access {
	return { grants: BUILTIN_ROLES.workspace_superadmin.grants, builtinRole: 'agent_superadmin' };
}

/** Grants from a role held on one agent: workspace permissions dropped, since they count only for all agents. */
export function agentScopedGrants(grants: Grant[]): Grant[] {
	return grants.filter((g) => !isWorkspacePermission(g.permission));
}

/** The union of several roles. A built-in role's grants come from code, by key. */
export function accessFromRoles(roles: { key: string | null; isBuiltin: boolean; grants: Grant[] | null }[]): Access {
	const grants: Grant[] = [];
	let builtinRole: RbacRole | null = null;
	for (const role of roles) {
		const builtin = role.isBuiltin && role.key && Object.hasOwn(BUILTIN_ROLES, role.key) ? BUILTIN_ROLES[role.key as BuiltinRoleKey] : null;
		grants.push(...(builtin ? builtin.grants : normalizeGrants(role.grants ?? [])));
		const level = builtin?.key === 'workspace_superadmin' ? 'agent_superadmin' : builtin?.key;
		if (isRbacRole(level) && (!builtinRole || RANK[level] > RANK[builtinRole])) builtinRole = level;
	}
	return { grants, builtinRole };
}

/** The globs a person's `knowledge:write` grants cover: null for all of `KNOWLEDGE_GLOBS`, `[]` for none. */
export function knowledgePathsOf(access: Access): string[] | null {
	const writes = access.grants.filter((g) => g.permission === 'knowledge:write');
	if (writes.some((g) => !g.paths)) return null;
	return [...new Set(writes.flatMap((g) => g.paths!))];
}

/** What the person may change in a draft, or null for everything. */
export function writeScopeOf(access: Access): WriteScope | null {
	const code = access.grants.some((g) => g.permission === 'code:write');
	const knowledge = knowledgePathsOf(access);
	return code && knowledge === null ? null : { code, knowledge };
}

/** Holds `code:write` or `knowledge:write`, so may change some part of a draft. */
export function canEditDraft(access: Access): boolean {
	return access.grants.some((g) => isWriteGrant(g.permission));
}

/** Whether the person may change some part of tree path `path`. See `writeRefusal` for subagent frontmatter. */
export function canWriteTreePath(access: Access, path: string): boolean {
	return canEditDraft(access) && canWritePath(writeScopeOf(access), path);
}

/**
 * The tree globs the assistant's file tools may read, or null for every file. Without `code:write`, reads stay
 * on the files the person may edit: their `knowledge:write` paths, or all of prompts and knowledge.
 */
export function readPathsOf(access: Access): string[] | null {
	if (access.grants.some((g) => g.permission === 'code:write')) return null;
	const knowledge = knowledgePathsOf(access);
	return knowledge?.length ? knowledge : [...KNOWLEDGE_GLOBS];
}

export function permissionsFor(access: Access): Permission[] {
	const granted = new Set<Permission>(access.grants.map((g) => g.permission));
	return PERMISSIONS.filter((p) => granted.has(p));
}

export function isViewPermission(permission: string): permission is ViewPermission {
	return (VIEW_PERMISSIONS as readonly string[]).includes(permission);
}

/**
 * The grants a stored `versions:write` (before code and knowledge were split) stands for. Without paths it is
 * both; with paths it keeps the ones inside prompts and knowledge, and `dropped` lists the rest.
 */
export function splitLegacyWrite(paths: readonly string[] | undefined): { grants: Grant[]; dropped: string[] } {
	if (!paths) return { grants: [{ permission: 'code:write' }, { permission: 'knowledge:write' }], dropped: [] };
	const kept = paths.filter(isKnowledgePattern);
	return { grants: kept.length ? [{ permission: 'knowledge:write', paths: kept }] : [], dropped: paths.filter((p) => !isKnowledgePattern(p)) };
}

/** Legacy names expanded, so stored roles from before view rules and the code/knowledge split read the same. Safe on any stored list. */
export function normalizeGrants(grants: readonly Grant[]): Grant[] {
	const out: Grant[] = [];
	for (const grant of grants) {
		const legacy = LEGACY_PERMISSIONS[grant.permission as string];
		if (legacy) out.push(...legacy.map((permission) => ({ permission })));
		else if ((grant.permission as string) === 'versions:write') out.push(...splitLegacyWrite(grant.paths).grants);
		else out.push(grant);
	}
	return withoutRepeats(out);
}

/** Several old names now stand for one permission, so drop repeated plain grants. */
function withoutRepeats(grants: Grant[]): Grant[] {
	const seen = new Set<Permission>();
	return grants.filter((g) => {
		if (g.paths || g.views || g.actions) return true;
		if (seen.has(g.permission)) return false;
		seen.add(g.permission);
		return true;
	});
}

/**
 * Whether `access` allows `permission`. For a view permission, a `ViewTarget` asks about that view; without
 * one the answer is whether any rule grants it. Tree paths: `canWriteTreePath`.
 */
export function can(access: Access, permission: Permission, target?: ViewTarget | null): boolean {
	if (target && isViewPermission(permission)) return canOnView(access, permission, target);
	return permissionsFor(access).includes(permission);
}

/** Whether some grant of `permission` covers every view, so no view filter applies. */
export function canOnAllViews(access: Access, permission: ViewPermission): boolean {
	return access.grants.some((g) => g.permission === permission && !g.views);
}

/**
 * Whether the assistant may copy every customer of the agent into the person's sandbox (docs/impl/136).
 * The copy is every user and can leave as a download, so it takes reading and exporting every view, and bash.
 */
export function canDeepAnalyze(access: Access): { ok: true } | { ok: false; reason: string } {
	if (!can(access, 'data:analyze')) return { ok: false, reason: 'Your role does not allow deep analysis (data:analyze).' };
	if (!can(access, 'code:write')) return { ok: false, reason: 'Deep analysis needs the right to edit agent code (code:write), since the assistant works on the copy with bash.' };
	const everyView = (permission: ViewPermission) => access.grants.some((g) => g.permission === permission && (!g.views || g.views.includes('*')));
	if (!everyView('users:read') || !everyView('users:export'))
		return { ok: false, reason: 'Deep analysis copies every customer, so it needs users:read and users:export on every view. Your role reaches only some views.' };
	return { ok: true };
}

function canOnView(access: Access, permission: ViewPermission, target: ViewTarget): boolean {
	if (access.grants.some((g) => g.permission === permission && viewPatternsMatch(g.views, target))) return true;
	if (!target.mine) return false;
	if (permission === 'view:update' || permission === 'view:delete') return true;
	// A creator reaches users through their view only where they already could through its parent.
	return !!target.parent && canOnView(access, permission, target.parent);
}

/**
 * Whether the person may change what a view reaches: its filters, slug or `allowGroups`. Besides
 * `view:update` it takes `users:read` on every view, except on a view they created under a view they
 * can read: there the parent's filters always apply, so a change can only narrow.
 */
export function canChangeViewReach(access: Access, target: ViewTarget): boolean {
	if (!can(access, 'view:update', target)) return false;
	if (canOnAllViews(access, 'users:read')) return true;
	return !!target.mine && !!target.parent && can(access, 'users:read', target.parent);
}

/** Creating a view needs `views:create` on its parent, or on every view for a top-level one. */
export function canCreateViewUnder(access: Access, parent: ViewTarget | null): boolean {
	return parent ? can(access, 'views:create', parent) : canOnAllViews(access, 'views:create');
}

/**
 * Moving a view needs what creating it at the new place needs, plus `view:update` on it. A view someone
 * else made changes what other people's rules reach when it moves, so that also takes `users:read` on every view.
 */
export function canMoveView(access: Access, target: ViewTarget, newParent: ViewTarget | null): boolean {
	return can(access, 'view:update', target) && canCreateViewUnder(access, newParent) && (!!target.mine || canOnAllViews(access, 'users:read'));
}

/** Whether a rule other than "every view" names this view, which lets it past `allowGroups`. */
export function namesView(access: Access, target: ViewTarget): boolean {
	return access.grants.some((g) => isViewPermission(g.permission) && !!g.views && !g.views.includes('*') && viewPatternsMatch(g.views, target));
}

/** Whether the person may see the view at all: some view permission on it, or they created it. */
export function canSeeView(access: Access, target: ViewTarget): boolean {
	return !!target.mine || VIEW_PERMISSIONS.some((p) => canOnView(access, p, target));
}

/**
 * A role's grants with writes tied to `editPaths`, the 130 form of a 129 membership limit. A member gains
 * `knowledge:write` on those paths; code edits, publish and promote go, as 129 had them for a bounded writer.
 * Paths outside prompts and knowledge are dropped (`splitLegacyWrite`).
 */
export function grantsLimitedTo(role: RbacRole, editPaths: string[]): Grant[] {
	const kept = BUILTIN_ROLES[role].grants.filter((g) => !isWriteGrant(g.permission) && !['versions:publish', 'drafts:delete'].includes(g.permission));
	return [...kept, ...splitLegacyWrite(editPaths).grants];
}

const GLOB_CHARS =/[*?[\]{}()!+@]/;

/** Whether someone with `access` may hand out `grant`. Nobody gives a grant they don't hold. */
export function canGive(access: Access, grant: Grant): boolean {
	if (!can(access, grant.permission)) return false;
	if (isViewPermission(grant.permission)) return canGiveViews(access, grant.permission, grant.views);
	if (grant.permission === 'actions:run') return !grant.actions ? canRunEveryAction(access) : grant.actions.every((id) => canRunAction(access, id));
	if (grant.permission !== 'knowledge:write') return true;
	const mine = knowledgePathsOf(access);
	if (!mine) return true;
	if (!grant.paths) return false;
	// Glob containment is undecidable in general, so a bounded holder may give back their own patterns or plain paths they cover.
	return grant.paths.every((p) => mine.includes(p) || (!GLOB_CHARS.test(p) && canEditPath(mine, p)));
}

/** Whether some `actions:run` grant names this action or has no list (every action). */
export function canRunAction(access: Access, actionId: string): boolean {
	return access.grants.some((g) => g.permission === 'actions:run' && (!g.actions || g.actions.includes(actionId)));
}

export function canRunEveryAction(access: Access): boolean {
	return access.grants.some((g) => g.permission === 'actions:run' && !g.actions);
}

const MAX_ACTION_IDS = 200;

function validateActionIds(value: unknown): { ok: true; actions: string[] | null } | { ok: false; error: string } {
	if (!Array.isArray(value) || value.length > MAX_ACTION_IDS || value.some((id) => typeof id !== 'string' || !id.trim() || id.length > 120)) {
		return { ok: false, error: 'actions must be a list of action ids' };
	}
	const ids = [...new Set(value.map((id: string) => id.trim()))];
	return { ok: true, actions: ids.includes('*') ? null : ids };
}

/** A view rule is giveable when the giver holds it on every view, or holds a rule with each of the same patterns. */
function canGiveViews(access: Access, permission: ViewPermission, views: string[] | undefined): boolean {
	if (canOnAllViews(access, permission)) return true;
	if (!views) return false;
	const mine = new Set(access.grants.filter((g) => g.permission === permission).flatMap((g) => g.views ?? []));
	return views.every((v) => mine.has(v));
}

/** Checks a role's grants before they are saved. Legacy names are saved expanded. */
export function validateGrants(value: unknown): { ok: true; grants: Grant[] } | { ok: false; error: string } {
	if (!Array.isArray(value)) return { ok: false, error: 'grants must be an array' };
	const grants: Grant[] = [];
	for (const raw of value) {
		const permission = raw?.permission;
		const legacy = typeof permission === 'string' ? LEGACY_PERMISSIONS[permission] : undefined;
		if (legacy) {
			grants.push(...legacy.map((p) => ({ permission: p })));
			continue;
		}
		if (permission === 'versions:write') {
			const paths = raw.paths == null ? { ok: true as const, editPaths: null } : validateEditPaths(raw.paths);
			if (!paths.ok) return paths;
			grants.push(...splitLegacyWrite(paths.editPaths ?? undefined).grants);
			continue;
		}
		if (!(PERMISSIONS as readonly string[]).includes(permission)) return { ok: false, error: `Unknown permission: ${String(permission)}` };
		if (raw.actions !== undefined && raw.actions !== null) {
			if (permission !== 'actions:run') return { ok: false, error: `Only actions:run takes actions, not ${permission}` };
			const actions = validateActionIds(raw.actions);
			if (!actions.ok) return actions;
			grants.push(actions.actions ? { permission, actions: actions.actions } : { permission });
			continue;
		}
		if (raw.views !== undefined && raw.views !== null) {
			if (!isViewPermission(permission)) return { ok: false, error: `Only ${VIEW_PERMISSIONS.join(', ')} take views, not ${permission}` };
			const views = validateViewPatterns(raw.views);
			if (!views.ok) return views;
			grants.push(views.views.includes('*') ? { permission } : { permission, views: views.views });
			continue;
		}
		if (raw.paths === undefined || raw.paths === null) {
			grants.push({ permission });
			continue;
		}
		if (permission !== 'knowledge:write') return { ok: false, error: `Only knowledge:write takes paths, not ${permission}` };
		const paths = validateEditPaths(raw.paths);
		if (!paths.ok) return paths;
		const outside = (paths.editPaths ?? []).filter((p) => !isKnowledgePattern(p));
		if (outside.length) return { ok: false, error: `knowledge:write paths must be inside prompts/ or knowledge/: ${outside.join(', ')}` };
		grants.push({ permission, paths: paths.editPaths ?? [] });
	}
	return { ok: true, grants: withoutRepeats(grants) };
}
